/**
 * Watermark removal by context synthesis (inpainting).
 *
 * The previous implementation tried to *recover* the pixels underneath a
 * semi-transparent watermark by algebraically inverting the blend:
 *
 *     observed = alpha * white + (1 - alpha) * original
 *     original = (observed - alpha) / (1 - alpha)
 *
 * That approach cannot work reliably on lossy sources. JPEG's 4:2:0 chroma
 * subsampling and 8x8 DCT quantisation destroy the exact linear relationship
 * the inversion depends on, so `alpha` itself is only ever recoverable to
 * within a few percent. Worse, the `1 / (1 - alpha)` term amplifies that error
 * without bound as alpha approaches 1, which is why the old code produced black
 * blotches on light backgrounds and grey haze over dark ink.
 *
 * Inpainting sidesteps the whole problem. Rather than trying to recover a
 * signal that is gone, we mark the watermark as an unknown region and ask a
 * solver to synthesise a continuation of the surrounding pixels that is
 * visually plausible. Nothing is being "recovered", so nothing can be
 * "recovered wrongly", and the technique generalises to any watermark the user
 * can mask rather than only the one hardcoded Gemini star.
 */

import { ensureOpenCv, requireOpenCv } from './openCvProvider';
import type { OpenCvMat, OpenCvRuntime } from './openCvRuntime';
import { maskBounds, type Box } from './mask';

export type InpaintMethod = 'telea' | 'ns';

export interface InpaintOptions {
  /** Radius of the neighbourhood considered at each inpainted pixel, in px. */
  radius?: number;
  method?: InpaintMethod;
}

export const DEFAULT_INPAINT_RADIUS = 5;

/**
 * Extra context kept around the mask crop. Telea propagates inward from the
 * mask boundary, so the result depends on the pixels just outside it; a crop
 * taken flush to the mask edge synthesises from a ring that has already had
 * parts of itself filled, which smears.
 */
function contextPadding(radius: number): number {
  return Math.max(12, Math.round(radius * 3));
}

/**
 * Fill the masked region of an RGBA buffer from surrounding context.
 *
 * Returns a new buffer; `rgba` is left untouched. Pixels outside the mask are
 * copied through byte-for-byte, and the alpha channel is never modified, so
 * existing transparency survives intact.
 */
export async function inpaintRgba(
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
  mask: Uint8Array,
  options: InpaintOptions = {}
): Promise<Uint8ClampedArray> {
  const radius = Math.max(1, Math.round(options.radius ?? DEFAULT_INPAINT_RADIUS));
  const method = options.method ?? 'telea';

  const tight = maskBounds(mask, width, height);
  if (!tight) {
    // Nothing masked — nothing to synthesise.
    return rgba.slice();
  }

  const pad = contextPadding(radius);
  const crop = clampBox(tight, width, height, pad);

  await ensureOpenCv();
  return runInpaint(requireOpenCv(), rgba, width, height, mask, crop, radius, method);
}

function clampBox(box: Box, width: number, height: number, pad: number): Box {
  const x = Math.max(0, box.x - pad);
  const y = Math.max(0, box.y - pad);
  const right = Math.min(width, box.x + box.width + pad);
  const bottom = Math.min(height, box.y + box.height + pad);
  return { x, y, width: right - x, height: bottom - y };
}

function runInpaint(
  cv: OpenCvRuntime,
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
  mask: Uint8Array,
  crop: Box,
  radius: number,
  method: InpaintMethod
): Uint8ClampedArray {
  const { x: cx, y: cy, width: cw, height: ch } = crop;

  // cv.inpaint accepts 8-bit 1- or 3-channel input only — handing it RGBA
  // throws. Drop alpha for the solve and restore it afterwards, which also
  // keeps the original alpha exactly as-is.
  const rgb = new Uint8Array(cw * ch * 3);
  const maskBytes = new Uint8Array(cw * ch);

  for (let y = 0; y < ch; y++) {
    let src = ((cy + y) * width + cx) * 4;
    let dst = y * cw * 3;
    let m = y * cw;
    for (let x = 0; x < cw; x++) {
      rgb[dst] = rgba[src];
      rgb[dst + 1] = rgba[src + 1];
      rgb[dst + 2] = rgba[src + 2];
      // cv.inpaint treats any non-zero as "hole"; keep it strictly binary so a
      // partially painted stroke cannot be read as a softer constraint.
      maskBytes[m] = mask[(cy + y) * width + cx + x] ? 255 : 0;
      src += 4;
      dst += 3;
      m += 1;
    }
  }

  const srcMat = new cv.Mat(ch, cw, cv.CV_8UC3);
  const maskMat = new cv.Mat(ch, cw, cv.CV_8UC1);
  const dstMat = new cv.Mat();

  try {
    srcMat.data.set(rgb);
    maskMat.data.set(maskBytes);

    cv.inpaint(
      srcMat,
      maskMat,
      dstMat,
      radius,
      method === 'ns' ? cv.INPAINT_NS : cv.INPAINT_TELEA
    );

    const solved = dstMat.data;
    const out = rgba.slice();
    for (let y = 0; y < ch; y++) {
      let dst = y * cw * 3;
      let target = ((cy + y) * width + cx) * 4;
      for (let x = 0; x < cw; x++) {
        // Only the hole itself was modified by the solver; everything else is
        // whatever OpenCV echoed back, so take the hole pixels only and leave
        // known pixels bit-exact.
        if (maskBytes[y * cw + x]) {
          out[target] = solved[dst];
          out[target + 1] = solved[dst + 1];
          out[target + 2] = solved[dst + 2];
        }
        dst += 3;
        target += 4;
      }
    }
    return out;
  } finally {
    // Mats live in the WASM heap; skipping delete() leaks it for the lifetime
    // of the page, and this function runs once per processed image.
    release(srcMat);
    release(maskMat);
    release(dstMat);
  }
}

function release(mat: OpenCvMat): void {
  try {
    mat.delete();
  } catch {
    // Already released, or never allocated. Nothing useful to do.
  }
}